import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { RefreshCw, Trash2, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

type FileUploadRow = Tables<'file_uploads'>;

interface ProcessingQueueProps {
  organizationId: string;
  mandateId: string;
  refreshKey?: number;
}

interface QueueItem {
  id: string;
  fileName: string;
  status: string;
  createdAt: string;
  processingStatus: FileUploadRow['processing_status'];
  transactionsCount: number;
}

const statusLabel = (status: FileUploadRow['processing_status']) => {
  switch (status) {
    case 'processing':
      return 'Processing…';
    case 'pending':
      return 'Queued';
    case 'error':
      return 'Processing error';
    default:
      return 'Queued';
  }
};

export const ProcessingQueue: React.FC<ProcessingQueueProps> = ({ organizationId, mandateId, refreshKey = 0 }) => {
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [processingItems, setProcessingItems] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  useEffect(() => {
    if (!organizationId || !mandateId) {
      setQueueItems([]);
      return;
    }

    const loadQueue = async () => {
      const { data, error } = await supabase
        .from('file_uploads')
        .select('id, file_name, processing_status, created_at, transactions_count')
        .eq('organization_id', organizationId)
        .eq('mandate_id', mandateId)
        .in('processing_status', ['pending', 'processing'])
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Failed to load processing queue', error);
        setQueueItems([]);
        return;
      }

      const mapped = (data ?? []).map<QueueItem>((row) => ({
        id: row.id,
        fileName: row.file_name,
        status: statusLabel(row.processing_status),
        createdAt: row.created_at,
        processingStatus: row.processing_status,
        transactionsCount: row.transactions_count ?? 0,
      }));

      setQueueItems(mapped);
    };

    void loadQueue();
  }, [organizationId, mandateId, refreshKey]);

  const queueHeader = useMemo(() => {
    if (queueItems.length === 0) return 'Processing Queue';
    return `Processing Queue (${queueItems.length})`;
  }, [queueItems.length]);

  const handleRetry = async (fileId: string) => {
    setProcessingItems(prev => new Set(prev).add(fileId));
    
    try {
      // Get file upload details
      const { data: fileUpload, error: fetchError } = await supabase
        .from('file_uploads')
        .select('*')
        .eq('id', fileId)
        .single();

      if (fetchError || !fileUpload) {
        toast({
          title: 'Error',
          description: 'Failed to fetch file details',
          variant: 'destructive',
        });
        return;
      }

      // Update status to processing
      await supabase
        .from('file_uploads')
        .update({ processing_status: 'processing', error_message: null })
        .eq('id', fileId);

      // Trigger AI processing
      const { error: processError } = await supabase.functions.invoke('ai-process-transactions', {
        body: {
          fileUploadId: fileId,
          mandateId: fileUpload.mandate_id,
          organizationId: fileUpload.organization_id,
          priority: 1,
        },
      });

      if (processError) {
        toast({
          title: 'Processing Failed',
          description: processError.message,
          variant: 'destructive',
        });
        
        await supabase
          .from('file_uploads')
          .update({ 
            processing_status: 'error',
            error_message: processError.message 
          })
          .eq('id', fileId);
      } else {
        toast({
          title: 'Processing Started',
          description: 'File is now being processed',
        });
      }

      // Reload queue
      const { data } = await supabase
        .from('file_uploads')
        .select('id, file_name, processing_status, created_at, transactions_count')
        .eq('organization_id', organizationId)
        .eq('mandate_id', mandateId)
        .in('processing_status', ['pending', 'processing'])
        .order('created_at', { ascending: true });

      if (data) {
        const mapped = data.map<QueueItem>((row) => ({
          id: row.id,
          fileName: row.file_name,
          status: statusLabel(row.processing_status),
          createdAt: row.created_at,
          processingStatus: row.processing_status,
          transactionsCount: row.transactions_count ?? 0,
        }));
        setQueueItems(mapped);
      }
    } catch (error) {
      console.error('Retry error:', error);
      toast({
        title: 'Error',
        description: 'Failed to retry processing',
        variant: 'destructive',
      });
    } finally {
      setProcessingItems(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleDelete = async (fileId: string) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    setProcessingItems(prev => new Set(prev).add(fileId));

    try {
      // Get file path first
      const { data: fileUpload } = await supabase
        .from('file_uploads')
        .select('file_path')
        .eq('id', fileId)
        .single();

      // Delete from storage
      if (fileUpload?.file_path) {
        await supabase.storage
          .from('file-uploads')
          .remove([fileUpload.file_path]);
      }

      // Delete record
      const { error } = await supabase
        .from('file_uploads')
        .delete()
        .eq('id', fileId);

      if (error) {
        toast({
          title: 'Error',
          description: 'Failed to delete file',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Success',
          description: 'File deleted successfully',
        });
        
        // Remove from queue
        setQueueItems(prev => prev.filter(item => item.id !== fileId));
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete file',
        variant: 'destructive',
      });
    } finally {
      setProcessingItems(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-brand-border">
      <h3 className="text-lg font-bold text-brand-primary mb-4">{queueHeader}</h3>
      {queueItems.length === 0 ? (
        <p className="text-sm text-brand-text-secondary">No files are currently waiting for processing.</p>
      ) : (
        <div className="space-y-3">
          {queueItems.map((item) => {
            const isProcessingItem = processingItems.has(item.id);
            const isStuck = item.processingStatus === 'processing' && 
              new Date().getTime() - new Date(item.createdAt).getTime() > 5 * 60 * 1000; // 5 min

            return (
              <div 
                key={item.id} 
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  isStuck ? 'bg-orange-50 border-orange-200' : 'bg-brand-secondary border-transparent'
                }`}
              >
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                  isStuck ? 'bg-orange-100' : 'bg-white border border-brand-border'
                }`}>
                  {isStuck ? (
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                  ) : (
                    <i className="fa-solid fa-file-lines text-brand-text-secondary text-sm"></i>
                  )}
                </div>
                
                <div className="flex-grow min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-brand-text-primary truncate">
                      {item.fileName}
                    </p>
                    {isStuck && (
                      <span className="text-xs px-2 py-0.5 bg-orange-200 text-orange-800 rounded-full">
                        Stuck
                      </span>
                    )}
                  </div>
                  <p className={`text-xs ${isStuck ? 'text-orange-600' : 'text-brand-text-secondary'}`}>
                    {item.status} • {new Date(item.createdAt).toLocaleTimeString()}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-brand-text-secondary">
                    {item.transactionsCount ? `${item.transactionsCount} tx` : 'Queued'}
                  </span>
                  
                  <button
                    onClick={() => handleRetry(item.id)}
                    disabled={isProcessingItem}
                    className="p-2 hover:bg-white rounded-lg transition-colors disabled:opacity-50"
                    title="Retry processing"
                  >
                    <RefreshCw className={`h-4 w-4 text-brand-primary ${isProcessingItem ? 'animate-spin' : ''}`} />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={isProcessingItem}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete file"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
